import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import { Activity, Slot } from 'services';
import { DateTimePicker } from '@material-ui/pickers';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  datepicker: {
    marginTop: 0
  }
}));

const schema = {
  title: {
    presence: { allowEmpty: false, message: "is required" },
  },
  content: {
    presence: { allowEmpty: false, message: "is required" }
  },
  timestamp: {
    presence: { allowEmpty: false, message: "is required" }
  },
  status: {
    presence: { allowEmpty: false, message: "is required" }
  },
};

let mounted = false;

const CreateModal = props => {
  let { onCreate, reload, open, onClose, className, slot_id, ...rest } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher();

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      title: "",
      content: "",
      timestamp: moment(),
      status: "draft",
      slot: null
    },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    const errors = validate(formState.values, schema);

    mounted && setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  useEffect(() => {
    mounted = true;
    return () => { mounted = false };
  }, []);

  useEffect(() => {
    setFormState({
      isValid: false,
      values: {
        title: "",
        content: "",
        timestamp: moment(),
        status: "draft",
        slot: null
      },
      touched: {},
      errors: {}
    });
  }, [open]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState({
      ...formState,
      touched: {
        title: true,
        content: true,
        timestamp: true,
        status: true,
      }
    });
    if (Object.keys(formState.errors).length) return;
    mounted && setLoading(true);
    runAsyncBlock(async () => {
      const { title, content, timestamp, status, slot } = formState.values;
      if (slot_id) {
        let { model } = await Slot.create_activity(slot_id, { title, content, timestamp, status });
        onCreate(model.id);
        onClose();
      } else {
        await Activity.create({ title, content, timestamp, status, slot });
        onCreate();
        reload();
      }
    }).finally(() => {
      mounted && setLoading(false);
    });

  }

  const handleFieldChange = event => {
    event.persist();
    mounted && setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  const handleDateChange = (date, field) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [field]: date
      },
      touched: {
        ...formState.touched,
        [field]: true
      }
    }));
  };

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Activity
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField name="title" label="Title" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <DateTimePicker
                  className={classes.datepicker}
                  fullWidth
                  disableFuture
                  variant="inline"
                  format="YYYY/MM/DD - HH:mm"
                  id="timestamp"
                  label="Timestamp"
                  name="timestamp"
                  autoOk
                  value={formState.values.timestamp}
                  required
                  onChange={(date) =>
                    handleDateChange(date, "timestamp")
                  }
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="status" label="Status" formState={formState} handleFieldChange={handleFieldChange} dropdown options={[{ label: "Draft", value: "draft" }, { label: "Published", value: "published" }]} />
              </Grid>
              <Grid item md={12} xs={12}>
                <CustTextField multiline name="content" label="Content" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required, multiline } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
      multiline={multiline}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateModal;